import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getAuthHeaders } from '../../../contexts/AuthContext';
import { API } from '../execShared';

export default function AvailabilitySection() {
  const [availability, setAvailability] = useState([]);
  const [month, setMonth] = useState(new Date());
  const [, setLoading] = useState(true);
  const [, setSaving] = useState(false);
  // Per-offering scope. Empty array = applies to ALL offerings (legacy).
  const [scopeOfferings, setScopeOfferings] = useState([]);
  const [offeringTypes, setOfferingTypes] = useState([]);

  useEffect(() => {
    axios.get(`${API}/availability/my`, { headers: getAuthHeaders() }).then(r => setAvailability(r.data)).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API}/offerings/types`).then(r => setOfferingTypes(r.data.types || [])).catch(() => {});
  }, []);

  const year = month.getFullYear();
  const mo = month.getMonth();
  const firstDay = new Date(year, mo, 1).getDay();
  const daysInMonth = new Date(year, mo + 1, 0).getDate();
  const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });

  const byDate = {};
  availability.forEach(a => { byDate[a.date] = a.status; });

  const toggleDate = async (dateStr) => {
    const current = byDate[dateStr];
    let newStatus;
    if (!current || current === 'unavailable') newStatus = 'available';
    else if (current === 'available') newStatus = 'tentative';
    else newStatus = 'unavailable';

    setSaving(true);
    try {
      if (newStatus === 'unavailable') {
        await axios.delete(`${API}/availability/my/${dateStr}`, { headers: getAuthHeaders() });
        setAvailability(availability.filter(a => a.date !== dateStr));
      } else {
        await axios.post(`${API}/availability/my`, {
          dates: [dateStr], status: newStatus, offering_types: scopeOfferings
        }, { headers: getAuthHeaders() });
        const updated = availability.filter(a => a.date !== dateStr);
        updated.push({ date: dateStr, status: newStatus, offering_types: scopeOfferings });
        setAvailability(updated);
      }
    } catch { /* ignore transient save error */ } finally { setSaving(false); }
  };

  const statusColors = { available: 'bg-[#10B981]', tentative: 'bg-yellow-400', unavailable: 'bg-[#6B7280]' };
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6" data-testid="exec-availability-section">
      <div>
        <h2 className="font-display text-2xl font-bold">My <span className="text-[#FF4343]">Availability</span></h2>
        <p className="text-xs text-[#9CA3AF] mt-1">Click dates to mark your availability for engagements. Brands and admins see this when matching you to opportunities.</p>
      </div>

      {/* Per-offering scope picker */}
      <div className="bg-[#111827] border border-white/10 p-4" data-testid="availability-scope">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-[#FF4343] mb-0.5">Scope for new dates</p>
            <p className="text-xs text-[#9CA3AF]">
              {scopeOfferings.length === 0
                ? 'Currently: applies to ALL offering types (default). Filter below to limit.'
                : `Currently: only applies to ${scopeOfferings.length} selected offering type${scopeOfferings.length > 1 ? 's' : ''}.`}
            </p>
          </div>
          {scopeOfferings.length > 0 && (
            <button onClick={() => setScopeOfferings([])}
              data-testid="clear-scope"
              className="text-[10px] font-mono uppercase text-[#6B7280] hover:text-white border border-white/10 px-2 py-1">
              Reset to all
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {offeringTypes.map(o => {
            const active = scopeOfferings.includes(o.key);
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => setScopeOfferings(active
                  ? scopeOfferings.filter(k => k !== o.key)
                  : [...scopeOfferings, o.key])}
                data-testid={`scope-${o.key}`}
                className={`text-[10px] px-2.5 py-1 border transition-colors ${active
                  ? 'border-[#FF4343] bg-[#FF4343]/10 text-[#FF4343]'
                  : 'border-white/10 text-[#9CA3AF] hover:text-white'}`}>
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-[#111827] border border-white/10 p-5">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setMonth(new Date(year, mo - 1, 1))} className="text-[#6B7280] hover:text-white p-1"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-semibold">{monthName}</span>
          <button onClick={() => setMonth(new Date(year, mo + 1, 1))} className="text-[#6B7280] hover:text-white p-1"><ChevronRight className="w-4 h-4" /></button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-px mb-1">
          {['S','M','T','W','T','F','S'].map((d, i) => (
            <div key={i} className="text-center text-[9px] font-mono text-[#6B7280] uppercase py-1">{d}</div>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-7 gap-px" data-testid="availability-grid">
          {Array(firstDay).fill(null).map((_, i) => <div key={`e-${i}`} className="h-14 bg-[#090E33]/30" />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateStr = `${year}-${String(mo + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const status = byDate[dateStr];
            const isToday = dateStr === today;
            const isPast = dateStr < today;

            return (
              <div key={day}
                onClick={() => !isPast && toggleDate(dateStr)}
                className={`h-14 bg-[#090E33] border relative p-1.5 transition-colors
                  ${isPast ? 'opacity-30 cursor-not-allowed border-white/5' : 'cursor-pointer hover:border-white/20 border-white/5'}
                  ${isToday ? 'border-[#FF4343]/40' : ''}
                  ${status === 'available' ? 'bg-[#10B981]/10 border-[#10B981]/30' : ''}
                  ${status === 'tentative' ? 'bg-yellow-400/10 border-yellow-400/30' : ''}`}
                data-testid={`avail-${dateStr}`}
              >
                <span className={`text-[10px] ${isToday ? 'text-[#FF4343] font-bold' : 'text-[#6B7280]'}`}>{day}</span>
                {status && (
                  <span className={`absolute bottom-1.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full ${statusColors[status]}`} />
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex gap-4 mt-3 text-[9px] text-[#6B7280]">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#10B981]" /> Available</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" /> Tentative</span>
          <span>Click to cycle: Available → Tentative → Clear</span>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-[#111827] border border-white/10 p-4 text-center">
          <span className="font-mono text-2xl font-bold text-[#10B981]">{availability.filter(a => a.status === 'available').length}</span>
          <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1">Available Dates</p>
        </div>
        <div className="bg-[#111827] border border-white/10 p-4 text-center">
          <span className="font-mono text-2xl font-bold text-yellow-400">{availability.filter(a => a.status === 'tentative').length}</span>
          <p className="text-[9px] text-[#6B7280] font-mono uppercase mt-1">Tentative Dates</p>
        </div>
      </div>
    </div>
  );
}


